"use strict";
/**
 * Wearable Context Schemas
 *
 * Data shapes that flow through the report pipeline via the orchestration bridge.
 * WearablesDomain produces WearableContext; WeeklyReportDomain consumes it.
 * The two domains never import each other — this schema is the contract.
 *
 * Architecture: WearableInsightSelector deterministically scores and selects
 * the top 2-3 wearable-skin observations. The LLM only rephrases the output
 * for brand voice — it never interprets patterns or selects what to show.
 *
 * @see docs/superpowers/plans/2026-03-14-wearables-integration-phase1.md
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LifestyleInsightCandidateSchema = exports.MaybeWearableContextSchema = exports.NoWearableContextSchema = exports.WearableContextSchema = exports.CompoundSignalFlagSchema = exports.TrendConcordanceSchema = exports.CorrelationMatchSchema = exports.SignalAssessmentSchema = exports.WindowStatsSchema = exports.WearableSelectedInsightSchema = exports.WearableTemperatureContextSchema = exports.WearableStressContextSchema = exports.WearableActivityContextSchema = exports.WearableSleepContextSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("../api/enums");
// ============================================================================
// DOMAIN CONTEXT SCHEMAS (aggregated summaries for UI display)
// ============================================================================
exports.WearableSleepContextSchema = zod_1.z.object({
    avgDurationMinutes: zod_1.z.number(),
    avgEfficiency: zod_1.z.number().nullable(),
    avgHrvRmssd: zod_1.z.number().nullable(),
    daysWithData: zod_1.z.number(),
});
exports.WearableActivityContextSchema = zod_1.z.object({
    avgSteps: zod_1.z.number(),
    avgExerciseMinutes: zod_1.z.number(),
    totalExerciseCount: zod_1.z.number(),
    daysWithData: zod_1.z.number(),
});
exports.WearableStressContextSchema = zod_1.z.object({
    avgRestingHR: zod_1.z.number().nullable(),
    avgHrvRmssd: zod_1.z.number().nullable(),
    daysWithData: zod_1.z.number(),
});
exports.WearableTemperatureContextSchema = zod_1.z.object({
    avgSkinTempDelta: zod_1.z.number().nullable(),
    daysWithData: zod_1.z.number(),
});
// ============================================================================
// SELECTED INSIGHT (deterministically scored and ranked)
// ============================================================================
/**
 * A single wearable-skin observation selected by the deterministic scorer.
 * Max 3 per report, one per signal domain. Ready for LLM rephrasing.
 */
exports.WearableSelectedInsightSchema = zod_1.z.object({
    /** Wearable signal name, e.g. "sleep_efficiency" */
    signal: zod_1.z.string(),
    /** Human-readable signal label, e.g. "sleep quality" */
    signalLabel: zod_1.z.string(),
    /** Signal average for current report period */
    currentPeriodAvg: zod_1.z.number(),
    /** Signal average for prior period (null if first report) */
    priorPeriodAvg: zod_1.z.number().nullable(),
    /** Days of signal data in current period */
    daysWithData: zod_1.z.number(),
    /** Linked skin metric tech name, e.g. "hydration" */
    skinMetricTechName: zod_1.z.string(),
    /** Metric alert score from computeMetricAlertScore (MCID + acute) */
    metricAlertScore: zod_1.z.number(),
    /** Clinical evidence strength for this signal→metric link */
    evidenceStrength: zod_1.z.enum(['strong', 'medium']),
    /** Composite score used for ranking (higher = more notable) */
    compositeScore: zod_1.z.number(),
    /** Epistemic confidence tier for narrative language */
    epistemicTier: zod_1.z.enum(['high', 'moderate', 'low']),
});
// ============================================================================
// SIGNAL ASSESSMENT — output of SignalAssessmentService
// ============================================================================
/**
 * Statistics for a window of wearable data (acute or baseline).
 * CV is only computed for signals that track variability (sleep, exercise).
 */
exports.WindowStatsSchema = zod_1.z.object({
    median: zod_1.z.number(),
    /** Coefficient of variation (stdev / median). Null if not tracked for this signal. */
    cv: zod_1.z.number().nullable(),
    daysWithData: zod_1.z.number(),
});
/**
 * Per-signal output from SignalAssessmentService.
 * Combines medical band position, personal baseline comparison, and acute window stats.
 * Internal only — never passed directly to users.
 */
exports.SignalAssessmentSchema = zod_1.z.object({
    /** Internal signal key, e.g. 'hrv_rmssd' */
    signal: zod_1.z.string(),
    /** User-safe display name, e.g. 'stress recovery' */
    displayName: zod_1.z.string(),
    /** Medical population band the user falls in */
    medicalBand: zod_1.z.enum(['low', 'reduced', 'normal', 'elevated']),
    /** 28-day personal baseline stats. Null if fewer than MINIMUM_DAYS_FOR_BASELINE days. */
    baseline: exports.WindowStatsSchema.nullable(),
    /** Most recent 7-day window stats */
    acute: exports.WindowStatsSchema,
    /** Percent deviation of acute median from baseline median. Null if no baseline. */
    deviationPercent: zod_1.z.number().nullable(),
    /** Trend direction. 'stable' if deviation is below threshold or no baseline. */
    direction: zod_1.z.enum(['declining', 'stable', 'rising']),
    /** Pre-written appearance-level context string for band position */
    skinContext: zod_1.z.string(),
    /** Pre-written appearance-level context for acute change. Null if direction is stable. */
    acuteContext: zod_1.z.string().nullable(),
    /** Pre-written context if CV exceeds variability threshold. Null if not applicable. */
    variabilityContext: zod_1.z.string().nullable(),
    /** How quickly this signal affects skin appearance */
    temporalLag: zod_1.z.object({
        acuteHours: zod_1.z.number(),
        peakDays: zod_1.z.number(),
        chronicWeeks: zod_1.z.number(),
    }),
    /** True if acute median falls in a different medical band than baseline median */
    thresholdCrossing: zod_1.z.boolean(),
    /** Supporting context values (not separately assessed, shown on card) */
    supportingValues: zod_1.z
        .array(zod_1.z.object({
        label: zod_1.z.string(),
        value: zod_1.z.number(),
        unit: zod_1.z.string(),
    }))
        .optional(),
});
// ============================================================================
// CORRELATION MATCH — output of CorrelationMatcherService
// ============================================================================
/**
 * A single lag-aligned, direction-validated match between a wearable signal
 * change and a skin metric event. Produced by CorrelationMatcherService.
 * Internal only — never passed directly to users.
 */
exports.CorrelationMatchSchema = zod_1.z.object({
    /** Internal signal key, e.g. 'hrv_rmssd' */
    signal: zod_1.z.string(),
    /** User-safe display name, e.g. 'stress recovery' */
    signalDisplayName: zod_1.z.string(),
    /** Stella skin metric tech name, e.g. 'redness' */
    skinMetric: zod_1.z.enum(enums_1.techNameEnum),
    /** Whether the matched skin event was an MCID crossing or an acute signal */
    skinEventType: zod_1.z.enum(['mcid_crossing', 'acute_signal']),
    /** Whether the skin metric moved in an improving or worsening direction */
    skinEventDirection: zod_1.z.enum(['improvement', 'worsening']),
    /** Composite correlation score (higher = stronger match) */
    correlationScore: zod_1.z.number(),
    /** Full signal assessment context for this signal */
    signalAssessment: exports.SignalAssessmentSchema,
    /**
     * Estimated lag in days between the signal change and the skin event.
     * Derived from temporalLag.peakDays in the reference band.
     */
    lagDays: zod_1.z.number(),
    /** Pre-written appearance-level context string for band position */
    skinContext: zod_1.z.string(),
    /** Pre-written appearance-level context for acute change. Null if direction is stable. */
    acuteContext: zod_1.z.string().nullable(),
    /** Pre-written context if CV exceeds variability threshold. Null if not applicable. */
    variabilityContext: zod_1.z.string().nullable(),
});
// ============================================================================
// TREND CONCORDANCE — 3-week parallel signal + skin metric trend
// ============================================================================
/**
 * Records whether a wearable signal and a skin metric have been trending
 * in the same direction over 3 consecutive weeks. Used as a boost factor
 * in the correlation scorer.
 */
exports.TrendConcordanceSchema = zod_1.z.object({
    /** Internal signal key, e.g. 'hrv_rmssd' */
    signal: zod_1.z.string(),
    /** Stella skin metric tech name, e.g. 'redness' */
    skinMetric: zod_1.z.enum(enums_1.techNameEnum),
    /** Direction of the signal over the 3-week window */
    signalTrend: zod_1.z.enum(['improving', 'stable', 'worsening']),
    /** Direction of the skin metric over the 3-week window */
    metricTrend: zod_1.z.enum(['improving', 'stable', 'worsening']),
    /** True if both signal and metric are moving in the same direction */
    concordant: zod_1.z.boolean(),
    /** Number of consecutive weeks observed (always 3 for Phase 1) */
    weeksObserved: zod_1.z.number(),
});
// ============================================================================
// COMPOUND SIGNAL FLAG — multiple degraded signals firing at once
// ============================================================================
/**
 * Raised when 2+ signals are simultaneously in a degraded state (low/reduced
 * band AND declining direction). The biological impact compounds when multiple
 * signals degrade together.
 */
exports.CompoundSignalFlagSchema = zod_1.z.object({
    /** Signal keys that are currently degraded */
    degradedSignals: zod_1.z.array(zod_1.z.string()),
    /** Number of degraded signals */
    count: zod_1.z.number(),
    /** Pre-written, appearance-level context string. TGA-safe. */
    compoundContext: zod_1.z.string(),
});
// ============================================================================
// WEARABLE CONTEXT (passed to WeeklyReportDomain via orchestration bridge)
// ============================================================================
exports.WearableContextSchema = zod_1.z.object({
    hasData: zod_1.z.literal(true),
    daysWithData: zod_1.z.number(),
    sleep: exports.WearableSleepContextSchema.nullable(),
    activity: exports.WearableActivityContextSchema.nullable(),
    stress: exports.WearableStressContextSchema.nullable(),
    temperature: exports.WearableTemperatureContextSchema.nullable(),
    /** Top 2-3 deterministically selected insights for LIFESTYLE_INSIGHTS section */
    selectedInsights: zod_1.z.array(exports.WearableSelectedInsightSchema),
    /** Signal assessments from SignalAssessmentService */
    signalAssessments: zod_1.z.array(exports.SignalAssessmentSchema).optional(),
    /** Correlation matches from CorrelationMatcherService */
    correlationMatches: zod_1.z.array(exports.CorrelationMatchSchema).optional(),
    /** Compound signal flag */
    compoundFlag: exports.CompoundSignalFlagSchema.nullable().optional(),
    /** Raw daily values per signal for sparkline rendering */
    dailySignalValues: zod_1.z
        .record(zod_1.z.string(), zod_1.z.array(zod_1.z.object({
        date: zod_1.z.string(),
        value: zod_1.z.number(),
    })))
        .optional(),
});
exports.NoWearableContextSchema = zod_1.z.object({
    hasData: zod_1.z.literal(false),
});
exports.MaybeWearableContextSchema = zod_1.z.discriminatedUnion('hasData', [
    exports.WearableContextSchema,
    exports.NoWearableContextSchema,
]);
// ============================================================================
// LIFESTYLE INSIGHT CANDIDATE — output of Component 7 curation step
// ============================================================================
/**
 * A fully enriched candidate for the LIFESTYLE_INSIGHTS report section.
 * Assembled from Track C output, correlation matcher, reference bands, and Track B.
 * Produced by the curation step (Component 7, Step 1) before LLM rephrasing.
 */
exports.LifestyleInsightCandidateSchema = zod_1.z.object({
    // From Track C
    signal: zod_1.z.string(),
    signalRank: zod_1.z.number().int(),
    lifestyleProse: zod_1.z.string(),
    // From correlation matcher
    matchedSkinEvent: zod_1.z
        .object({
        metric: zod_1.z.enum(enums_1.techNameEnum),
        direction: zod_1.z.enum(['improvement', 'worsening']),
        eventType: zod_1.z.enum(['mcid_crossing', 'acute_signal']),
        confidence: zod_1.z.string(),
    })
        .nullable(),
    correlationScore: zod_1.z.number(),
    // From reference bands
    skinContext: zod_1.z.string(),
    acuteContext: zod_1.z.string().nullable(),
    variabilityContext: zod_1.z.string().nullable(),
    // From Track B
    competingRoutineExplanation: zod_1.z.string().nullable(),
});
//# sourceMappingURL=context.js.map